/**
 * Unified Action Buttons Component
 * 
 * Single source of truth for rendering ALL action types across co-pilots.
 * 
 * Action Categories:
 * - Navigational: OPEN_ENTITY, NAVIGATE → Small inline buttons
 * - Mutating: UPDATE_ITEM, CREATE_ITEM, LINK_ITEMS, etc. → Handled by parent (UnifiedActionReview)
 * - Non-Actions: AWAIT_FEEDBACK → Filtered out, never rendered
 */

import React from 'react';
import { ExternalLink, Plus, ChevronRight, FileText, Link2, Trash2, GitMerge, Scissors } from 'lucide-react';
import { cn } from '../../../lib/utils';

// Actions that should NOT render any button (exported for use in UnifiedActionReview)
export const NON_RENDERABLE_ACTIONS = new Set(['AWAIT_FEEDBACK', 'CONFIRM_ACTION']);

// Navigational actions - render as small inline buttons
const NAVIGATIONAL_ACTIONS = new Set(['OPEN_ENTITY', 'NAVIGATE', 'VIEW_ITEM']);

// Mutating actions - render via UnifiedActionReview (parent handles these)
const MUTATING_ACTIONS = new Set([
  'UPDATE_ITEM', 'CREATE_ITEM', 'DELETE_ITEM', 
  'LINK_ITEMS', 'UNLINK_ITEMS', 
  'MERGE_ITEMS', 'SPLIT_ITEM',
  'BULK_UPDATE', 'BULK_CREATE', 'BULK_LINK',
  'APPLY_UPDATES', 'SUGGEST_SPLIT', 'ADD_METRICS'
]);

/**
 * Get icon for action type
 */
const getActionIcon = (action) => {
  const type = (action.type || action.action || '').toUpperCase();
  switch (type) {
    case 'OPEN_ENTITY':
    case 'VIEW_ITEM':
      return <ExternalLink className="w-3.5 h-3.5" />;
    case 'CREATE_ITEM':
    case 'CREATE_FEATURE_REQUEST':
      return <Plus className="w-3.5 h-3.5" />;
    case 'NAVIGATE':
      return <ChevronRight className="w-3.5 h-3.5" />;
    case 'LINK_ITEMS':
      return <Link2 className="w-3.5 h-3.5" />;
    case 'DELETE_ITEM':
      return <Trash2 className="w-3.5 h-3.5" />;
    case 'MERGE_ITEMS':
      return <GitMerge className="w-3.5 h-3.5" />;
    case 'SPLIT_ITEM':
      return <Scissors className="w-3.5 h-3.5" />;
    default:
      return <FileText className="w-3.5 h-3.5" />;
  }
};

/**
 * Get label for action
 */
const getActionLabel = (action) => {
  // Use explicit label if provided
  if (action.label) return action.label;
  
  const type = (action.type || action.action || '').toUpperCase();
  switch (type) {
    case 'OPEN_ENTITY':
    case 'VIEW_ITEM':
      return action.entity_type ? `Open ${action.entity_type}` : 'Open';
    case 'CREATE_FEATURE_REQUEST':
      return action.title || 'Create Feature Request';
    case 'NAVIGATE':
      return action.destination || 'Go to page';
    case 'CREATE_ITEM':
      return action.entity_type ? `Create ${action.entity_type}` : 'Create';
    case 'UPDATE_ITEM':
      return 'Update';
    case 'LINK_ITEMS':
      return action.target_type ? `Link to ${action.target_type}` : 'Link';
    case 'DELETE_ITEM':
      return 'Delete';
    case 'MERGE_ITEMS':
      return 'Merge';
    case 'SPLIT_ITEM':
      return 'Split';
    default:
      return 'Action';
  }
};

/**
 * Single Action Button - Small inline style (Executive AI gold standard)
 */
export const ActionButton = ({ action, onAction, className }) => {
  const type = (action.type || action.action || '').toUpperCase();
  
  // Don't render non-renderable actions
  if (NON_RENDERABLE_ACTIONS.has(type)) {
    return null;
  }
  
  return (
    <button
      onClick={() => onAction(action)}
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium",
        "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300",
        "rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/50",
        "transition-colors border border-indigo-200 dark:border-indigo-800",
        className
      )}
    >
      {getActionIcon(action)}
      <span className="max-w-[200px] truncate">{getActionLabel(action)}</span>
    </button>
  );
};

/**
 * Filter actions - removes non-renderable actions
 */
export const filterRenderableActions = (actions) => {
  if (!actions || !Array.isArray(actions)) return [];
  return actions.filter(a => {
    const type = (a.type || a.action || '').toUpperCase();
    return !NON_RENDERABLE_ACTIONS.has(type);
  });
};

/**
 * Categorize actions into navigational and mutating
 */
export const categorizeActions = (actions) => {
  const filtered = filterRenderableActions(actions);
  return {
    navigational: filtered.filter(a => NAVIGATIONAL_ACTIONS.has((a.type || a.action || '').toUpperCase())),
    mutating: filtered.filter(a => MUTATING_ACTIONS.has((a.type || a.action || '').toUpperCase())),
    other: filtered.filter(a => {
      const type = (a.type || a.action || '').toUpperCase();
      return !NAVIGATIONAL_ACTIONS.has(type) && !MUTATING_ACTIONS.has(type);
    })
  };
};

/**
 * Action Buttons Row - Renders navigational actions as inline buttons
 * Mutating actions should be handled separately via UnifiedActionReview
 */
export const ActionButtonsRow = ({ actions, onAction, className }) => {
  if (!actions || actions.length === 0) return null;
  
  const { navigational, other } = categorizeActions(actions);
  const inlineActions = [...navigational, ...other];
  
  if (inlineActions.length === 0) return null;
  
  return (
    <div className={cn("flex flex-wrap gap-2 mt-2", className)}>
      {inlineActions.map((action, idx) => (
        <ActionButton 
          key={`${action.type || action.action}_${idx}`} 
          action={action} 
          onAction={onAction} 
        />
      ))}
    </div>
  );
};

/**
 * Check if actions array has mutating actions that need UnifiedActionReview
 */
export const hasMutatingActions = (actions) => {
  if (!actions || !Array.isArray(actions)) return false;
  return actions.some(a => MUTATING_ACTIONS.has((a.type || a.action || '').toUpperCase()));
};

/**
 * Get mutating actions from array
 */
export const getMutatingActions = (actions) => {
  if (!actions || !Array.isArray(actions)) return [];
  return actions.filter(a => MUTATING_ACTIONS.has((a.type || a.action || '').toUpperCase()));
};

export default ActionButton;
